// §lab-requisition — what Beluga actually issues for a lab-only visit.
//
// Corrected 2026-08-24 after Natasha (Beluga) answered the question directly:
//
//   "there is no document requisition expected for labTestingAutoAuthorization
//    visit type. Please refer to the success response for the visit creation
//    for the approval for the lab test — it's the confirmation we provide for
//    the requested lab test"
//
// The earlier model here (shipped 2026-08-21) inferred a third state — "a
// requisition is needed in NY/NJ but this visit type can't issue one" — from
// the premise that an in-person draw site refuses anyone without a slip. That
// premise was ours, not Beluga's, and it produced the opposite harm from the
// one it was meant to fix: NY/NJ patients were told at checkout that their
// care team would send them a requisition after ordering. Nothing issues one,
// so that is a promise no one can keep.
//
// The correct model has two states:
//   NO_DOCUMENT  this visit type issues no requisition; the order confirmation
//                (the visit-creation success response) IS the authorization
//   UNKNOWN      a visit type we have no ruling on — assert nothing
//
// Note for anyone extending this: a visit type that DOES need a prescription
// is not this flow at all. Per the same thread, that is a real visit type with
// belugaOrdersLabs:false, and results a patient wants reviewed without a
// prescription go in under labAnalysis.

export const REQUISITION_NEED = {
  // Beluga issues no requisition document; the order confirmation is the approval.
  NO_DOCUMENT: 'no-document',
  // No ruling for this visit type. Make NO claim either way — a confident wrong
  // answer in either direction is what stranded a patient the first two times.
  UNKNOWN: 'unknown',
};

// Visit types Beluga has confirmed issue no requisition document.
const NO_REQUISITION_DOCUMENT = new Set([
  'labtestingautoauthorization',
  'labanalysis',
  'labanalysissync',
]);

const normVisitType = (v) => String(v || '').toLowerCase().replace(/[\s_-]/g, '');

/**
 * Which requisition state applies to a visit type.
 * State of residence is deliberately NOT an input: Beluga's answer is about the
 * visit type, and nothing we have says the answer varies by state.
 * @param {string} visitType the Beluga visit type being submitted
 */
export function resolveRequisitionNeed(visitType) {
  return NO_REQUISITION_DOCUMENT.has(normVisitType(visitType))
    ? REQUISITION_NEED.NO_DOCUMENT
    : REQUISITION_NEED.UNKNOWN;
}

// Completion-screen copy. The NO_DOCUMENT line must never leave the patient
// waiting on a document, and must never send them chasing their care team for
// one either.
export function requisitionCompletionCopy(need) {
  switch (need) {
    case REQUISITION_NEED.NO_DOCUMENT:
      return 'Your lab test is approved — your order confirmation is the authorization, so there’s no requisition slip to wait for. We’ll email your draw-site details shortly.';
    default:
      return "Your lab order has been received. We'll email you next steps shortly.";
  }
}
